import { adminBillingService } from '@/services/admin-billing.service';
import type { AdminFilters, PlanConfig } from '@/types/admin.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useBillingOverview() {
  return useQuery({
    queryKey: ['admin-billing-overview'],
    queryFn: () => adminBillingService.getOverview(),
  });
}

export function useAdminInvoices(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-invoices', filters],
    queryFn: () => adminBillingService.getInvoices(filters),
  });
}

export function useAdminPlans() {
  return useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => adminBillingService.getPlans(),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Partial<PlanConfig> }) => adminBillingService.updatePlan(uuid, data),
    onSuccess: () => { toast.success('Plan mis à jour'); qc.invalidateQueries({ queryKey: ['admin-plans'] }); },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<PlanConfig, 'uuid'>) => adminBillingService.createPlan(data),
    onSuccess: () => { toast.success('Plan créé'); qc.invalidateQueries({ queryKey: ['admin-plans'] }); },
    onError: () => toast.error('Erreur lors de la création'),
  });
}

export function useRevenueData(period: string = '12m') {
  return useQuery({
    queryKey: ['admin-revenue', period],
    queryFn: () => adminBillingService.getRevenueData(period),
  });
}

export function useFailedPayments(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-failed-payments', filters],
    queryFn: () => adminBillingService.getFailedPayments(filters),
  });
}

export function useRetryPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceUuid: string) => adminBillingService.retryPayment(invoiceUuid),
    onSuccess: () => { toast.success('Paiement relancé'); qc.invalidateQueries({ queryKey: ['admin-failed-payments'] }); },
    onError: () => toast.error('Erreur lors de la relance du paiement'),
  });
}
